import { RuleModel } from 'src/types/rule.model';
import React, { useState } from 'react';
import { Alert, IconList } from '@americanexpress/dls-react';
import { DLSSize } from 'src/types/dls-size.enum';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import styles from './dragRule.module.scss';
import { Formik } from 'formik';
import { getRuleInitialValues, getRuleValidationSchema, matchingRules } from 'src/utils/rule.util';
import RuleRow from 'src/components/atomic/molecules/ruleRow/RuleRow';
import { CategoryModel } from 'src/types/category.model';
import ApplyButton from 'src/components/atomic/atoms/applyButton/ApplyButton';
import DeleteButton from 'src/components/atomic/atoms/deleteButton/DeleteButton';
import ruleStyles from 'src/styles/rule.module.scss';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DragRuleFormikWrapperProps {
    rule: RuleModel;
    ruleItems: RuleModel[];
    pathname: string;
    categories: CategoryModel[];
    handleSubmit: (rule: RuleModel) => Promise<void>;
    handleDelete: (rule: RuleModel) => Promise<void>;
    index: number;
    isMinWidthMedium?: boolean;
}

export default function DragRuleFormikWrapper({
    rule,
    ruleItems,
    pathname,
    handleSubmit,
    handleDelete,
    isMinWidthMedium,
    categories,
    index
}: DragRuleFormikWrapperProps): JSX.Element {
    const [showRuleAlreadyExistsError, setShowRuleAlreadyExistsError] = useState(false);

    return (
        <Formik
            initialValues={getRuleInitialValues(rule)}
            key={rule.id}
            enableReinitialize
            validationSchema={getRuleValidationSchema()}
            onSubmit={async (values) => {
                setShowRuleAlreadyExistsError(false);
                const updatingRule: RuleModel = {
                    ...rule,
                    matcher: values.ruleMatcher,
                    transactionName: values.ruleTransactionName,
                    groupUid: values.ruleGroup
                };

                let error = false;
                ruleItems?.forEach((existingRule, itemIndex) => {
                    if (matchingRules(updatingRule, existingRule) && itemIndex !== index) {
                        error = true;
                        setShowRuleAlreadyExistsError(true);
                        return null;
                    }
                });

                if (!error) {
                    await handleSubmit(updatingRule);
                }
            }}
        >
            {({ dirty }) => (
                <DragRule
                    rule={rule}
                    categories={categories}
                    pathname={pathname}
                    isMinWidthMedium={isMinWidthMedium}
                    showApplyButton={dirty}
                    showRuleAlreadyExistsError={showRuleAlreadyExistsError}
                    handleDelete={handleDelete}
                />
            )}
        </Formik>
    );
}

export interface DragRuleProps {
    rule: RuleModel;
    categories: CategoryModel[];
    pathname: string;
    handleDelete: (rule: RuleModel) => Promise<void>;
    isMinWidthMedium?: boolean;
    showApplyButton?: boolean;
    showRuleAlreadyExistsError?: boolean;
}

export function DragRule({
    rule,
    categories,
    pathname,
    isMinWidthMedium,
    showApplyButton,
    showRuleAlreadyExistsError,
    handleDelete
}: DragRuleProps): JSX.Element {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: rule.id
    });

    const applyButton: JSX.Element = (
        <ApplyButton
            showIcon
            data-cy={Selectors.Transactions.APPLY_RULE_BUTTON}
            aria-label={LiteralsService.getLiteral('Rule.applyRule')}
        />
    );

    const deleteButton: JSX.Element = (
        <DeleteButton
            text={!isMinWidthMedium && LiteralsService.getLiteral('Rule.deleteRule')}
            aria-label={LiteralsService.getLiteral('Rule.deleteRule')}
            data-cy={Selectors.Transactions.DELETE_RULE_BUTTON}
            onClick={async () => await handleDelete(rule)}
            className={ruleStyles.deleteButton}
        />
    );

    // transform and transition are used to animate the row when it is dragged
    const draggableRowStyle = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div ref={setNodeRef} style={draggableRowStyle} data-cy={Selectors.Transactions.DRAG_RULE}>
            <RuleRow
                rule={rule}
                pathname={pathname}
                formStyle={styles.ruleRow}
                categories={categories}
                icon={
                    <IconList
                        size={DLSSize.EXTRA_SMALL}
                        className={styles.listIcon}
                        data-cy={Selectors.Transactions.DRAG_ICON}
                        title={LiteralsService.getLiteral('DragRule.drag')}
                        aria-roledescription={LiteralsService.getLiteral('DragRule.drag')}
                        // attributes add the draggable props to the row, including accessibility props
                        {...attributes}
                        // listeners makes the icon the drag handle
                        {...listeners}
                    />
                }
                button={showApplyButton ? applyButton : deleteButton}
            />
            {showRuleAlreadyExistsError && (
                <Alert
                    id="ruleAlreadyExistsAlert"
                    className={styles.alert}
                    data-cy={Selectors.Transactions.RULE_ALREADY_EXISTS_ERROR}
                >
                    {LiteralsService.getLiteral('Rule.alreadyExists')}
                </Alert>
            )}
        </div>
    );
}
